// LoadingState.js — Stage-based progress UI for /api/analyze polling.
//
// Why stages rather than a single spinner?
//   The full pipeline (parallel news search + market-data + SEC filings
//   fetches, DeepSeek LLM, adversarial credibility audit, fact-check
//   pass) takes ~70-150 seconds end to end. With only a static
//   "loading..." string users abandon the page around the 90-120s mark
//   even though the backend is still making progress. Surfacing the
//   pipeline stages converts the wait into perceived progress.
//
// Time budget per stage (in seconds since the poll started):
//   0-12s    Searching news & SEC filings
//   12-45s   Cross-checking 10-K, 10-Q & 8-K disclosures
//   45-95s   Generating bull / bear thesis (DeepSeek)
//   95-130s  Adversarial credibility audit
//   130s+    Final fact-check pass
//
// All numbers are approximations of typical backend latencies. The
// stages will advance even if the backend is faster — the indicator is
// never blocking, it's a hint to the user.
import React, { useEffect, useState } from 'react';

const STAGES = [
  { at: 0,   label: 'Searching news and SEC filings…' },
  { at: 12,  label: 'Cross-checking 10-K, 10-Q & 8-K disclosures…' },
  { at: 45,  label: 'Generating bull / bear thesis…' },
  { at: 95,  label: 'Running adversarial credibility audit…' },
  { at: 130, label: 'Final fact-check pass — almost ready…' },
];

function LoadingState() {
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const tick = () => setElapsed(Math.floor((Date.now() - startedAt) / 1000));
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  // Pick the latest stage whose threshold we've passed.
  const stage = STAGES.reduce(
    (acc, s) => (elapsed >= s.at ? s : acc),
    STAGES[0]
  );

  return (
    <div className="mt-8" data-testid="analysis-loading-state">
      <div
        className="h-3 rounded-full overflow-hidden"
        style={{ backgroundColor: 'hsl(var(--gb-surface-2))' }}
      >
        <div className="h-full shimmer" style={{ width: '100%' }} />
      </div>
      <p
        className="text-center mt-4 text-sm font-medium"
        style={{ color: 'hsl(var(--gb-navy))' }}
        data-testid="analysis-loading-stage"
      >
        {stage.label}
      </p>
      <p
        className="text-center mt-1 text-xs"
        style={{ color: 'hsl(var(--gb-text-muted))' }}
        data-testid="analysis-loading-elapsed"
      >
        Reports usually take about 90 seconds. Elapsed: {elapsed}s.
      </p>
    </div>
  );
}

export default LoadingState;
